import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type HelpTab = 'functionality' | 'user-guide' | 'faq' | 'tour';

interface HelpDrawerContextValue {
  isOpen: boolean;
  activeTab: HelpTab;
  openHelp: (tab?: HelpTab) => void;
  closeHelp: () => void;
  setActiveTab: (tab: HelpTab) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const HelpDrawerContext = createContext<HelpDrawerContextValue | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function HelpDrawerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<HelpTab>('functionality');

  const openHelp = useCallback((tab: HelpTab = 'functionality') => {
    setActiveTab(tab);
    setIsOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value: HelpDrawerContextValue = {
    isOpen,
    activeTab,
    openHelp,
    closeHelp,
    setActiveTab,
  };

  return (
    <HelpDrawerContext.Provider value={value}>
      {children}
    </HelpDrawerContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useHelpDrawer() {
  const ctx = useContext(HelpDrawerContext);
  if (!ctx) throw new Error('useHelpDrawer must be used inside HelpDrawerProvider');
  return ctx;
}
